import React from 'react';
import styled from '@emotion/styled';
import { mqMin } from 'utils/responsive';

const Container = styled.div`
    width: 100%;
    padding: 0 10px;
    position: relative;
    display: flex;
    flex-direction: column;

    ${mqMin('NARROW')} {
        padding: 0;
    }
`;

const HeaderSection = styled.div`
    width: 100%;

    ${mqMin('NARROW')} {
        height: 83px;
    }
`;

const NumberCircle = styled.div`
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    background: #1695d4;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    top: -0.1rem;

    ${mqMin('SP')} {
        width: 4.5rem;
        height: 4.5rem;
        top: -0.5rem;
    }

    ${mqMin('NARROW')} {
        width: 58px;
        height: 58px;
        top: 0;
    }
`;

const NumberText = styled.div`
    color: #fff;
    text-align: center;
    font-family:
        'Zen Kaku Gothic Antique',
        -apple-system,
        Roboto,
        Helvetica,
        sans-serif;
    font-size: 1rem;
    top: -0.1rem;
    font-weight: 700;
    line-height: 180%;
    letter-spacing: 2px;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);

    ${mqMin('SP')} {
        font-size: 1.4rem;
        top: -0.5rem;
    }

    ${mqMin('NARROW')} {
        top: -0.2rem;
    }
`;

const TitleBar = styled.div`
    width: 100%;
    height: 2.5rem;
    border-radius: 4px;
    background: #1695d4;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1.4rem;
    z-index: 1;
    position: relative;

    ${mqMin('SP')} {
        height: 44px;
    }

    ${mqMin('NARROW')} {
        height: 3rem;
        margin-top: 1.8rem;
    }
`;

const TitleText = styled.div`
    color: #fff;
    text-align: center;
    font-family:
        'Zen Kaku Gothic Antique',
        -apple-system,
        Roboto,
        Helvetica,
        sans-serif;
    font-size: 1rem;
    font-weight: 700;
    line-height: 180%;
    letter-spacing: 2.2px;

    ${mqMin('SP')} {
        font-size: 1.3rem;
    }

    ${mqMin('NARROW')} {
        font-size: 1.2rem;
    }
`;

const ImageWrapper = styled.div`
    display: flex;
    justify-content: center;
    height: 10rem;
    margin-top: 0;

    ${mqMin('SP')} {
        height: 11.5rem;
        margin-top: 1.5rem;
    }

    ${mqMin('NARROW')} {
        margin-top: 2rem;
    }
`;

const Image = styled.img`
    width: 10rem;
    height: auto;

    ${mqMin('SP')} {
        width: 13rem;
    }

    ${mqMin('NARROW')} {
        width: 234px;
        height: 175px;
    }
`;

const MenuSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    position: relative;
    left: 0;
    width: 100%;
    margin-top: 15px;
    padding-bottom: 1rem;

    ${mqMin('NARROW')} {
        padding-bottom: 0;
    }
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: #1e4e66;
`;

const MenuItem = styled.button`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 30px;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: opacity 0.3s;

    &:hover {
        opacity: 0.7;
    }
`;

const MenuText = styled.span`
    color: #1e4e66;
    font-family:
        'Zen Kaku Gothic Antique',
        -apple-system,
        Roboto,
        Helvetica,
        sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    letter-spacing: 1.6px;

    ${mqMin('SP')} {
        font-size: 16px;
    }
`;

const MenuIconCircle = styled.div`
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #1e4e66;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const ArrowIcon = styled.svg`
    width: 8px;
    height: 10px;
`;

interface MenuItemData {
    text: string;
    onClick: () => void;
}

interface SectionPanelProps {
    number: string;
    title: string;
    imageUrl: string;
    imageAlt?: string;
    menuItems: MenuItemData[];
}

export const SectionPanel: React.FC<SectionPanelProps> = ({ number, title, imageUrl, imageAlt = '', menuItems }) => {
    return (
        <Container>
            <HeaderSection>
                <NumberCircle />
                <NumberText>{number}</NumberText>
                <TitleBar>
                    <TitleText>{title}</TitleText>
                </TitleBar>
            </HeaderSection>

            <ImageWrapper>
                <Image src={imageUrl} alt={imageAlt} />
            </ImageWrapper>

            <MenuSection>
                <Divider />
                {menuItems.map((item, index) => (
                    <React.Fragment key={index}>
                        <MenuItem onClick={item.onClick}>
                            <MenuText>{item.text}</MenuText>
                            <MenuIconCircle>
                                <ArrowIcon viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M1 0L7 5L1 10"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </ArrowIcon>
                            </MenuIconCircle>
                        </MenuItem>
                        <Divider />
                    </React.Fragment>
                ))}
            </MenuSection>
        </Container>
    );
};
